import { useEffect, useMemo } from "react";

import { compact, isEmpty, map, reject } from "lodash/fp";
import { shallow } from "zustand/shallow";

import type {
  Block,
  Marker,
} from "@app/analytics/components/PlayerControls/types";
import useSessionMetadata from "@app/analytics/MeetingReport/hooks/useSessionMetadata";
import useVideoStore from "@app/analytics/MeetingReport/stores/video";
import type { ReelClip, VideoReel } from "@app/analytics/MeetingReport/types";
import { getDurationFromClips } from "@app/analytics/MeetingReport/utils";
import {
  getBlocksAndMarkersFromHighlights,
  populateHighlights,
} from "@app/analytics/MeetingReport/utils/highlightUtils";
import { UPSELL_HIGHLIGHTS } from "@app/analytics/MeetingReport/utils/upsellPreviewContent";
import type { SessionHighlight } from "@app/common/types";

import useHighlightsQuery from "./useHighlightsQuery";
import useMeetingReport from "./useMeetingReport";

interface UseMeetingReportHighlights {
  blocks: Block[];
  /** All highlights, including any deleted ones */
  allHighlights: SessionHighlight[];
  /** Highlights to display (excludes any that are deleted) */
  highlights: SessionHighlight[];
  isInitialLoading: boolean;
  markers: Marker[];
}

const DEFAULT_OPTS = { enabled: true };

const makeHighlightReel = (
  highlights: SessionHighlight[]
): VideoReel | null => {
  if (isEmpty(highlights)) return null;

  const clips = compact(
    map((highlight): ReelClip | null => {
      if (
        highlight.isDeleted ||
        highlight.relativeStartTime == null ||
        highlight.relativeEndTime == null
      ) {
        return null;
      }
      return {
        startTimestamp: highlight.relativeStartTime,
        endTimestamp: highlight.relativeEndTime,
      };
    }, highlights)
  );

  const duration = getDurationFromClips(clips);

  return {
    clips,
    playing: false,
    name: "highlights",
    duration,
  };
};

const useMeetingReportHighlights = (
  opts: {
    enabled: boolean;
  } = DEFAULT_OPTS
): UseMeetingReportHighlights => {
  const {
    data: { isRestrictedReport, shouldUpsellTranscriptEntities, startTime },
  } = useSessionMetadata({ enabled: opts.enabled });

  const { setHighlightReel, videoStartTimeOffset } = useVideoStore(
    (state) => ({
      setHighlightReel: state.setHighlightReel,
      videoStartTimeOffset: state.videoStartTimeOffset,
    }),
    shallow
  );

  const queryOpts = { enabled: opts.enabled && !isRestrictedReport };

  const {
    meetingStartTime,
    meetingDuration,
    isLoading: meetingReportIsLoading,
  } = useMeetingReport(queryOpts);

  const { data: highlights, isLoading: highlightsLoading } =
    useHighlightsQuery(queryOpts);

  const isInitialLoading = highlightsLoading || meetingReportIsLoading;
  const startTimeToUse = meetingStartTime ?? startTime;

  const populatedHighlights = useMemo(() => {
    if (shouldUpsellTranscriptEntities) return UPSELL_HIGHLIGHTS;
    if (!startTimeToUse || isRestrictedReport) return [];

    return populateHighlights(
      highlights ?? [],
      startTimeToUse,
      videoStartTimeOffset
    );
  }, [
    startTimeToUse?.toISO(),
    isInitialLoading,
    videoStartTimeOffset,
    highlights,
    isRestrictedReport,
    shouldUpsellTranscriptEntities,
  ]);

  useEffect(() => {
    if (isEmpty(populatedHighlights) || isRestrictedReport) return;

    const highlightReel = makeHighlightReel(populatedHighlights);
    setHighlightReel(highlightReel);
  }, [populatedHighlights, isRestrictedReport]);

  const { blocks, markers } = useMemo(() => {
    if (isRestrictedReport) return { blocks: [], markers: [] };

    return getBlocksAndMarkersFromHighlights(populatedHighlights);
  }, [populatedHighlights, isRestrictedReport, meetingDuration]);

  const filteredHighlights = reject({ isDeleted: true }, populatedHighlights);

  return {
    blocks,
    allHighlights: populatedHighlights,
    highlights: filteredHighlights,
    isInitialLoading,
    markers,
  };
};

export default useMeetingReportHighlights;
